import { AfterContentChecked, AfterContentInit, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, HostListener, Inject, Input, OnInit, Optional, Output, PLATFORM_ID, TemplateRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ControlContainer, FormGroupDirective, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { interval } from 'rxjs';
import { distinctUntilChanged, filter, finalize, takeWhile, timeout } from 'rxjs/operators';
import { Bank } from 'src/app/modules/dashboard/pages/payment-data/payment-data.component';

@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
    standalone: false
})
export class SelectComponent implements OnInit, AfterContentChecked, AfterContentInit {
	public selectedLabel: string = '';
	public savedBankLabel: Bank = { name: '', code: '' };
	public activeList: boolean = false;
	public openedOnce: boolean = false;
	public itemsToSave: any;
	public itemsState: any;
	public itemsCity: any;
	public itemsBank: any;
	public itemsNotFound: boolean = false;
	public selectedItems: any[] = [];

	@Input() public items!: any;
	@Input() public key!: string;
	@Input() public value!: string;
	@Input() public controlName!: string;
	@Input() public arrayName!: string;
	@Input() public arrayIndex!: number;
	@Input() public customClass: string = '';
	@Input() public placeHolder: string = '';
	@Input() public disabled: boolean = false;
	@Input() public isStandaloneValue: boolean = false;
	@Input() public hasClearButton?: boolean = false;
	@Input() public standaloneValue: any = null;
	@Input() public customTemplate: boolean = false;
	@Input() public item: any = null;
	@Input() public isCiiuLabel: boolean = false;
	@Input() public isState: boolean = false;
	@Input() public isCity: boolean = false;
	@Input() public isBank: boolean = false;
	@Input() public isBanksLoaded!: boolean;
	@Input() public disableSearch: boolean = true;
	@Input() multiple = false;
	@Input() emitObject = false;
	@Input() public directControl!: UntypedFormControl;

	@Output() blurEvent: EventEmitter<void> = new EventEmitter<void>();
	@Output() public changeEvent: EventEmitter<{ label: string; value: string }> = new EventEmitter<{ label: string; value: string }>();

	@ContentChild('header', { static: false }) public headerTemplateRef!: TemplateRef<any>;

	constructor(
		@Optional() private controlContainer: ControlContainer,
		private _cd: ChangeDetectorRef,
		private elementRef: ElementRef,
		@Inject(PLATFORM_ID) private platformId: Object
	) {}

	ngOnInit(): void {
		if (!this.isStandaloneValue && !this.isBank) {
			this.control?.valueChanges.pipe(distinctUntilChanged()).subscribe((value: any) => {
				if (this.multiple) {
					if (Array.isArray(value)) {
						this.selectedItems = this.items?.filter((item: any) => value.includes(item[this.key])) || [];
						this.selectedLabel = this.selectedItems.map(i => this.getValue(i)).join(', ');
					}
				} else {
					if (value == null) {
						this.selectedLabel = '';
					} else {
						const matchedItem = this.items?.find((item: any) => item[this.key] === value);
						if (matchedItem) {
							this.select(matchedItem);
						}
					}
				}

				this.changeEvent.emit({ label: this.selectedLabel, value: value });
				this._cd.detectChanges();
			});
		}
	}

	ngAfterContentInit(): void {
		if (!isPlatformBrowser(this.platformId)) return;
		if (this.isBank) {
			this.waitForBankItemsLoaded();
		}
		setTimeout(() => {
			this.itemsState = this.items;
			this.itemsCity = this.items;
			this.itemsToSave = this.items;
			this._cd.markForCheck();
		}, 1000);
	}

	get isBankContextLoaded(): boolean {
		return this.isBank && this.isBanksLoaded;
	}

	public get isEffectivelyDisabled(): boolean {
		return this.disabled || !!this.control?.disabled;
	}

	private waitForBankItemsLoaded(timeoutDuration = 10000, intervalDuration = 500): void {
		interval(intervalDuration)
		.pipe(
			filter(() => this.isBankContextLoaded),
			takeWhile(() => !this.isBankContextLoaded, true),
			timeout(timeoutDuration),
			finalize(() => {
				if (!this.isBankContextLoaded) {
					console.error('Timeout: os bancos não foram carregados a tempo.');
				}
			})
		)
		.subscribe({
			next: () => this.initializeItems(),
			error: err => console.error(err.message),
		});
	}

	clearSelection(): void {
		if (this.isEffectivelyDisabled) return;

		if (this.isStandaloneValue) {
			this.selectedLabel = '';
			this.changeEvent.emit({ label: '', value: '' });
		} else {
			if (this.multiple) {
				this.selectedItems = [];
				this.control.setValue([], { emitEvent: true });
				this.selectedLabel = '';
			} else {
				this.control.setValue(null);

				this.control.markAsTouched();
				this.control.markAsDirty();

				this.control.updateValueAndValidity();
				this.selectedLabel = '';
			}
		}
		this.activeList = false;
		this._cd.detectChanges();
	}

	initializeItems(): void {
		this.itemsBank = this.items;
		this._cd.markForCheck();
	}

	ngAfterContentChecked(): void {
		if (this.items && this.selectedLabel == '' && !this.isStandaloneValue && !this.isBank) {
			const selectedValue = this.control?.value;
			let matchedItem = this.items?.find((item: any) => item[this.key] === selectedValue);
			if (!matchedItem) {
				matchedItem = this.items?.find((item: any) => item[this.key] == selectedValue);
			}
			if (matchedItem) {
				this.select(matchedItem);
			}
		}
	}

	ngOnChanges() {
		if (this.isStandaloneValue && this.standaloneValue == null) {
			this.selectedLabel = '';
		}
		this.itemsCity = this.items;
	}

	onBlur() {
    this.control?.markAsTouched();
    this.blurEvent.emit();
}

	toggleList(): void {
		if (this.isEffectivelyDisabled) return;
		this.activeList = !this.activeList;
		this.openedOnce = true;
	}

	filterValue(event: any): void {
		if (this.isEffectivelyDisabled) return;

		const searchValue = event.value?.toLowerCase() || '';
		this.activeList = true;

		const itemsToFilter = this.getItemsToFilter();

		if (!searchValue) {
			this.items = itemsToFilter;
			this.itemsNotFound = false;
			this.selectedLabel = '';
			if (!this.isStandaloneValue) {
				this.control.setValue(null);
			}
			this.changeEvent.emit({ label: '', value: '' });
			return;
		}

		const filteredItems = new Set();
		for (const item of itemsToFilter) {
			if (this.matchesSearch(item, searchValue)) {
				filteredItems.add(item);
			}
		}

		this.items = Array.from(filteredItems);
		this.itemsNotFound = this.items.length === 0;
		// this.changeEvent.emit({ label: this.selectedLabel, value: searchValue });
	}

	getItemsToFilter(): any[] {
		if (this.isCiiuLabel) return this.itemsToSave;
		if (this.isState) return this.itemsState;
		if (this.isCity) return this.itemsCity;
		if (this.isBank) return this.itemsBank;
		return this.itemsToSave;
	}

	matchesSearch(item: any, searchValue: string): boolean {
		const searchLower = searchValue.toLowerCase();

		const includesSearchValue = (field: string | undefined): boolean => (field?.toLowerCase() || '').includes(searchLower);

		if (this.isCiiuLabel) {
			const fieldsToCheck = new Set([item?.code, item?.description, `${item?.code} - ${item?.description}`]);

			for (let field of fieldsToCheck) {
				if (includesSearchValue(field)) {
					return true;
				}
			}
			return false;
		}

		const additionalFields = [item?.name, item?.order, item?.plate, item?.city];

		return additionalFields.some(includesSearchValue);
	}

	get form(): UntypedFormGroup {
		return this.controlContainer.control as UntypedFormGroup;
	}

	get control(): UntypedFormControl {
		if (this.directControl) return this.directControl;
		if (this.arrayName) {
			const formArray = this.form.get(this.arrayName) as UntypedFormArray;
			return formArray.controls[this.arrayIndex].get(this.controlName) as UntypedFormControl;
		}
		return this.form.get(this.controlName) as UntypedFormControl;
	}

	public getValue(item: any): any {
		return item[this.value];
	}

	onItemClick(item: any): void {
		if (this.isEffectivelyDisabled) return;
		this.select(item);
	}

	select(item: any): void {
		if (!item) return;

		if (!this.isStandaloneValue && this.control.value === item[this.key]) {
			this.selectedLabel = this.buildLabel(item);
			this._cd.markForCheck();
			this._cd.detach();
			this._cd.reattach();
			this._cd.detectChanges();
			this.activeList = false;
			return;
		}

		this.savedBankLabel = item;
		this.selectedLabel = this.buildLabel(item);

		if (!this.isStandaloneValue) {
			this.control.setValue(item[this.key], { onlySelf: !!this.arrayName });
		}
		if (this.isStandaloneValue) {
			this.changeEvent.emit({ label: this.selectedLabel, value: item });
		}

		this.activeList = false;
		this._cd.detectChanges();
	}

	private buildLabel(item: any): string {
		if (this.isCiiuLabel) {
			const label = `${item[this.value]} - ${item.description}`;
			return label.length > 50 ? label.slice(0, 50) + '...' : label;
		}
		return item[this.value];
	}

	public isDescendant(parent: any, child: { parentNode: any }): boolean {
		let node = child.parentNode;
		while (node != null) {
			if (node == parent) {
				return true;
			}
			node = node.parentNode;
		}
		return false;
	}

@HostListener('document:click', ['$event'])
public clickout(event: any): void {

    const customList =
        this.elementRef.nativeElement.querySelector('.custom-select-list');

    if (
        this.activeList &&
        !this.isDescendant(customList, event.target) &&
        !event.target.classList.contains('custom-select-input')
    ) {

        this.activeList = false;

        if (!this.isStandaloneValue && this.control) {

            this.control.markAsTouched();

            if (!this.control.dirty) {
                this.control.markAsDirty();
            }

            this.control.updateValueAndValidity({
                emitEvent: false
            });
        }
    }
}

	toggleSelection(item: any): void {
		if (this.isEffectivelyDisabled) return;

		if (!this.multiple) {
			this.select(item);
			this.activeList = false;
			return;
		}

		const index = this.selectedItems.findIndex(i => i[this.key] === item[this.key]);
		if (index > -1) {
			this.selectedItems.splice(index, 1);
		} else {
			this.selectedItems.push(item);
		}

		this.updateControlValue();
	}
	isSelected(item: any): boolean {
		return this.selectedItems.some(i => i[this.key] === item[this.key]);
	}

	updateControlValue(): void {
		const value = this.selectedItems.map(i => i[this.key]);
		this.control?.setValue(value);
		this.selectedLabel = this.selectedItems.map(i => this.getValue(i)).join(', ');
	}
}